import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  ButtonGroup,
  Avatar,
  Stack,
  Divider,
  Badge,
  Link,
  Menu,
  MenuItem,
} from '@mui/material';
import { Add, Edit, Attachment, KeyboardArrowDown } from '@mui/icons-material';
import { sxConfig } from './workspace.style';
import { getContrastColor, getInitials } from '../../../utils/helper';
import dayjs from 'dayjs';
import { isExpired, isToday } from './workspaceUtils';
import TodoDialog from './TodoDialog/TodoDialog';
import workspaceService from '../../../service/CRM/workspace.service';
import { AutoCompleteSelectUI, FormLabelUI } from '../../Interface';
import simpletaskService from '../../../service/simpletask.service';
import useUsers from '../../../context/UsersContext';

const TodoCard = ({ selectedValues, autoCompleteOptions, autoCompleteData, setRefetch, handleNewCrmTask }) => {
  const { user } = useUsers();

  const defTodoDialogData = {
    open: false,
    projectId: null,
    data: {},
  };
  const [todoDialogData, setTodoDialogData] = useState(defTodoDialogData);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  const getTask = (simpleTaskId) => {
    setTodoDialogData((prev) => ({
      ...prev,
      open: true,
    }));

    workspaceService.getTaskById(simpleTaskId).then((data) => {
      setTodoDialogData((prev) => ({
        ...prev,
        data,
      }));
    });
  };

  const handleStatusUpdate = (simpleTaskId, statusId) => {
    simpletaskService.updateStatus({ simpleTaskId, statusId, updatedBy: user.userId }).then(() => {
      setRefetch(true);
    });
  };

  const handleNewTodo = () => {
    setTodoDialogData((prev) => ({
      ...prev,
      open: true,
      projectId: selectedValues.projectId,
    }));
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  return (
    <>
      <Paper sx={{ ...sxConfig.card, p: 3, minHeight: 340 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          mb={3}
        >
          <Typography variant="h6" fontWeight={700}>
            Teendők
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <>
              <ButtonGroup variant="contained">
                <Button startIcon={<Add />} onClick={handleNewTodo}>
                  Új teendő
                </Button>

                <Button size="small" onClick={(e) => setMenuAnchorEl(e.currentTarget)} sx={{ px: 1 }}>
                  <KeyboardArrowDown />
                </Button>
              </ButtonGroup>

              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    handleNewTodo();
                  }}
                >
                  Új teendő
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    handleNewCrmTask();
                  }}
                >
                  Új esemény
                </MenuItem>
              </Menu>
            </>
          </Stack>
        </Stack>

        {selectedValues.simpleTask?.map((task) => {
          const expired = isExpired(task.endDate);
          const dueToday = isToday(task.endDate);
          const lastComment = task.lastComment;

          return (
            <Box key={task.simpleTaskId}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Avatar
                  sx={{
                    background: task.responsiblePerson.color || '#0076f8',
                    color: getContrastColor(task.responsiblePerson.color || '#0076f8'),
                  }}
                >
                  {getInitials(task.responsiblePerson.nickName)}
                </Avatar>
                <Box flex={1}>
                  <Typography fontWeight={700}>{task.responsiblePerson.nickName}</Typography>
                  <Typography mt={0.5}>{task.simpleTaskName}</Typography>
                  {task.simpleTaskAttachment.map((attachment) => (
                    <Link
                      key={attachment.id}
                      href={`${process.env.REACT_APP_API_URL}/${attachment.path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      display="block"
                      mt={1}
                      sx={{ cursor: 'pointer', width: 'fit-content' }}
                    >
                      {attachment.name}
                    </Link>
                  ))}
                  {lastComment && (
                    <Box backgroundColor="#F3F8F9" py={1} px={2} borderRadius={2} mt={1.5}>
                      <Stack direction="row" spacing={1}>
                        <Avatar
                          sx={{
                            background: lastComment.user.color || '#0076f8',
                            color: getContrastColor(lastComment.user.color || '#0076f8'),
                          }}
                        >
                          {getInitials(lastComment.user.nickName)}
                        </Avatar>
                        <Box flex={1}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography color="text.secondary">
                              <Box component="span" fontWeight={700}>
                                {lastComment.user.nickName}
                              </Box>
                              {' - Utolsó jegyzet'}
                            </Typography>

                            <Typography color="text.secondary">
                              {dayjs(lastComment.createdAt).format('YYYY-MM-DD HH:mm')}
                            </Typography>
                          </Stack>
                          <Typography mt={0.5}>{lastComment.message}</Typography>
                        </Box>
                      </Stack>
                    </Box>
                  )}
                  <Stack direction="row" spacing={1} mt={2}>
                    <Box width={200}>
                      <AutoCompleteSelectUI
                        label={<FormLabelUI text="Státusz" />}
                        variant="outlined"
                        size="small"
                        dataset={autoCompleteOptions.taskStatus}
                        selectedValue={task.statusId}
                        selectedLabelValue={task.status.itemName}
                        onChange={(_e, newVal) => handleStatusUpdate(task.simpleTaskId, newVal.value)}
                        disableClearable
                      />
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => getTask(task.simpleTaskId)}
                    >
                      Szerkesztés
                    </Button>
                  </Stack>
                </Box>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  {task.simpleTaskAttachment.length > 0 && (
                    <Badge badgeContent={task.simpleTaskAttachment.length}>
                      <Attachment sx={{ color: 'text.secondary' }} />
                    </Badge>
                  )}
                  <Typography
                    color={expired ? 'error.main' : dueToday ? 'primary.main' : 'text.secondary'}
                    fontWeight={700}
                  >
                    {dueToday ? 'Ma' : dayjs(task.endDate).format('YYYY-MM-DD')}
                  </Typography>
                </Stack>
              </Stack>
              <Divider sx={{ my: 2 }} />
            </Box>
          );
        })}
      </Paper>
      <TodoDialog
        dialogData={todoDialogData}
        handleClose={() => setTodoDialogData(defTodoDialogData)}
        autoCompleteOptions={autoCompleteOptions}
        autoCompleteData={autoCompleteData}
        setRefetch={setRefetch}
      />
    </>
  );
};

export default TodoCard;
